import React from 'react';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { MapPin, Clock, ShoppingBag, CalendarDays } from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';

const MarketDayPage = () => {
  const { t } = useLanguage();
  const marketLocationLink = "https://www.google.com/maps/search/?api=1&query=Agorahof+10-20,+8224+BZ+Lelystad";
  
  return (
    <div className="py-12 md:py-20 bg-white pattern-bg">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <header className="text-center mb-12 md:mb-16">
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <ShoppingBag className="h-16 w-16 brand-text mx-auto mb-4" />
            <h1 className="text-4xl md:text-5xl font-extrabold brand-text mb-4">{t('marketDayPage.title')}</h1>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              {t('marketDayPage.subtitle')}
            </p>
          </motion.div>
        </header>

        <section aria-labelledby="market-details" className="grid md:grid-cols-2 gap-8 md:gap-12 items-center mb-16">
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <div className="aspect-video rounded-xl overflow-hidden shadow-xl border-4 border-white">
              <img src="/images/market/amsfood-market-stand.jpg" alt={t('marketDayPage.marketImageAlt')} className="w-full h-full object-cover" />
            </div>
          </motion.div>
          <motion.div
            className="space-y-6"
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <div className="flex items-start p-6 bg-card rounded-lg shadow-md border border-gray-100">
              <CalendarDays className="h-8 w-8 brand-text mr-4 mt-1 shrink-0" aria-hidden="true" />
              <div>
                <h2 id="market-day" className="text-xl font-semibold text-gray-800 mb-1">{t('marketDayPage.when')}</h2>
                <p className="text-muted-foreground">{t('marketDayPage.whenDetail')}</p>
              </div>
            </div>
            <div className="flex items-start p-6 bg-card rounded-lg shadow-md border border-gray-100">
              <Clock className="h-8 w-8 brand-text mr-4 mt-1 shrink-0" aria-hidden="true" />
              <div>
                <h2 id="market-time" className="text-xl font-semibold text-gray-800 mb-1">{t('marketDayPage.time')}</h2>
                <p className="text-muted-foreground">{t('marketDayPage.timeDetail')}</p>
              </div>
            </div>
            <div className="flex items-start p-6 bg-card rounded-lg shadow-md border border-gray-100">
              <MapPin className="h-8 w-8 brand-text mr-4 mt-1 shrink-0" aria-hidden="true" />
              <div>
                <h2 id="market-location" className="text-xl font-semibold text-gray-800 mb-1">{t('marketDayPage.location')}</h2>
                <p className="text-muted-foreground">{t('marketDayPage.locationDetail')}</p>
                <p className="text-sm text-muted-foreground mt-1">{t('marketDayPage.locationNote')}</p>
                <a href={marketLocationLink} target="_blank" rel="noopener noreferrer">
                  <Button variant="link" className="text-primary p-0 mt-2 hover:text-primary/80">
                    {t('marketDayPage.directions')}
                  </Button>
                </a>
              </div>
            </div>
          </motion.div>
        </section>
        
        <motion.section
          aria-labelledby="market-expectations"
          className="bg-secondary p-8 md:p-12 rounded-xl shadow-lg text-center mb-16 md:mb-20"
          initial={{ opacity: 0, scale: 0.95 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
        >
          <h2 id="market-expectations" className="text-3xl font-bold text-gray-800 mb-4">{t('marketDayPage.expectationsTitle')}</h2>
          <p className="text-muted-foreground mb-8 max-w-xl mx-auto">
            {t('marketDayPage.expectationsText')}
          </p>
          <div className="flex flex-wrap justify-center gap-4" role="list">
            <span role="listitem" className="bg-primary/10 text-primary px-4 py-2 rounded-full text-sm font-medium" dangerouslySetInnerHTML={{ __html: t('marketDayPage.expectationsTag1') }} />
            <span role="listitem" className="bg-primary/10 text-primary px-4 py-2 rounded-full text-sm font-medium" dangerouslySetInnerHTML={{ __html: t('marketDayPage.expectationsTag2') }} />
            <span role="listitem" className="bg-primary/10 text-primary px-4 py-2 rounded-full text-sm font-medium">{t('marketDayPage.expectationsTag3')}</span>
            <span role="listitem" className="bg-primary/10 text-primary px-4 py-2 rounded-full text-sm font-medium">{t('marketDayPage.expectationsTag4')}</span>
          </div>
        </motion.section>
      </div>
    </div>
  );
};

export default MarketDayPage;